import { LeaveHistoryDataTable } from '@/components/(leave_table)/components/LeaveHistoryDataTable'
import { leaveHistoryTableColumns } from '@/components/(leave_table)/components/leaveHistoryTableColumns'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useTRPC } from '@/trpc/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type } from 'arktype'

export const Route = createFileRoute('/dashboard/supervisor/leaves/history/')({
  component: RouteComponent,
  validateSearch: type({
    page: 'number = 1',
    pageSize: 'number = 50',
    employee: 'string = ""',
  }),
})

function RouteComponent() {
  const api = useTRPC()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const page = Math.max(1, Math.trunc(search.page))
  const pageSize = Math.min(Math.max(1, Math.trunc(search.pageSize)), 50)
  const debouncedEmployeeName = useDebouncedValue(search.employee, 800)

  const { data: historyResult, isPending } = useQuery({
    ...api.leave.getLeaveRequestHistory.queryOptions({
      page,
      pageSize,
      employeeName: debouncedEmployeeName ? debouncedEmployeeName : undefined,
    }),
  })

  function handlePaginationChange(nextPagination: { page: number; pageSize: number }): void {
    const normalizedPageSize = Math.min(Math.max(1, Math.trunc(nextPagination.pageSize)), 50)
    const normalizedPage =
      normalizedPageSize !== pageSize ? 1 : Math.max(1, Math.trunc(nextPagination.page))

    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        page: normalizedPage,
        pageSize: normalizedPageSize,
      }),
    })
  }

  const paginationPlaceholder = {
    page,
    pageSize,
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  }

  return (
    <DashboardShell>
      <DashboardHeader heading="Leave History" text="Every leave request regardless of status." />

      <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <CardContent className="pt-6">
          <div className="mb-4">
            <Input
              placeholder="Filter by employee name..."
              value={search.employee}
              onChange={(event) =>
                navigate({
                  search: (previousSearch) => ({
                    ...previousSearch,
                    page: 1,
                    employee: event.target.value,
                  }),
                })
              }
              className="max-w-xs"
            />
          </div>

          <LeaveHistoryDataTable
            columns={leaveHistoryTableColumns}
            data={historyResult?.data ?? []}
            isLoading={isPending}
            pagination={historyResult?.pagination ?? paginationPlaceholder}
            onPaginationChange={handlePaginationChange}
          />
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
